import { memoize, orderBy } from "lodash";
import getCoreUIStore from "../core/core-ui-store";
import getCorePopUpStore from "../core/core-popup-store";
import getKhattabatMatchingStore from "./matching-store";
import { computed, observable, runInAction } from "mobx";
import {
  TKhattabatMessage,
  TMatchDetail,
  TMatchDetailKeys,
  TMatchesConversations,
  TOneOOneMEnablementInformation
} from "../../Types";
import { apiGetOneOOneMatchesConversations } from "../../api/khattbat-api/api-call-get-one-o-one-matches-conversations";
import getKhattabatStore from ".";
import { apiCallGetOneOOneMMessages } from "../../api/khattbat-api/api-call-get-one-one-m-messages";
import { apiCallSetOneOOneMEnablement } from "../../api/khattbat-api/api-call-set-match-conversation-enablement";
import { apiCallIgnoreOneOOneM } from "../../api/khattbat-api/api-call-ignore-one-o-one-m";
import getKhattabatSelectsStore from "./selects-store";
import getCoreTimingStore from "../core/core-timing-store";
import { isDefinedArray, isPositiveNumber } from "et3-type-guards";
import getKhattabatFilterstore from "./filter-store";
import { apiCallSetOneOOneHighLight } from "../../api/khattbat-api/api-call-set-one-o-one-highlight";

class KhattabatResultsStore {
  matchesConversations = observable.box<TMatchesConversations>([]);

  findMatchIndexInConversationArray = <K extends TMatchDetailKeys>({
    parameter,
    parameterValue,
    matchesConversations
  }: {
    parameter: K;
    parameterValue: TMatchDetail[K];
    matchesConversations: TMatchesConversations;
  }): number => {
    return matchesConversations.findIndex(
      (object) => object[parameter] === parameterValue
    );
  };

  setClientConversations = (updatedConversations: TMatchesConversations) => {
    runInAction(() => {
      const currentConversations = this.matchesConversations.get();

      updatedConversations.forEach((matchDetails) => {
        const currentObjectIndex = this.findMatchIndexInConversationArray({
          parameter: "matchID",
          parameterValue: matchDetails.matchID,
          matchesConversations: currentConversations
        });

        if (isPositiveNumber(currentObjectIndex, true)) {
          const currentConversationIsOpenedState =
            this.matchesConversations.get()[currentObjectIndex]
              .isOpenedConversation;
          this.matchesConversations.get()[currentObjectIndex] = matchDetails;
          this.matchesConversations.get()[
            currentObjectIndex
          ].isOpenedConversation = currentConversationIsOpenedState;
        } else {
          this.matchesConversations.get().push(matchDetails);
        }
      });
      this.matchesConversations.set(currentConversations);
    });
  };

  resetClientConversations = () => {
    runInAction(() => {
      this.matchesConversations.set([]);
    });
  };

  getClientOneOOneMConversationID = (conversationID: string) => {
    return `${conversationID}_0`;
  };

  initializeConversationsProps = () => {
    this.matchesConversations.get().forEach((matchDetails) => {
      const conversationID = getKhattabatStore().getOneOOneConversationsId({
        alKhattabaUUID:
          getKhattabatMatchingStore().currentClientInformation.get()
            .alkhatabaUUID,
        userUUID: matchDetails.matchUUID
      });

      getKhattabatStore().createConversationProps(conversationID);
      getKhattabatStore().createConversationProps(
        this.getClientOneOOneMConversationID(conversationID)
      );
    });
  };

  getMatchesConversations = async () => {
    this.setClientConversations([]);
    const { alkhatabaUUID, userUUID } =
      getKhattabatMatchingStore().currentClientInformation.get();
    getCoreUIStore().setIsLoading("KHATTABAT_MATCHING_RESULTS", true);
    const { status, code, data } = await apiGetOneOOneMatchesConversations({
      alkhattabaUUID: alkhatabaUUID,
      clientUUID: userUUID,
      shouldUpdateUnread: false
    });
    getCoreUIStore().setIsLoading("KHATTABAT_MATCHING_RESULTS", false);
    if (status === "OK") {
      getKhattabatResultsStore().setClientConversations(data);
      this.initializeConversationsProps();
      getCoreTimingStore().initializeKhattabatPageMonitoring(
        "KHATTABAT_MATCHING_RESULTS"
      );
      const activeFilters =
        getKhattabatFilterstore().filter1o1MSelectedTypes.get();
      getKhattabatFilterstore().filter1o1MSelectedTypes.set(activeFilters);
      getKhattabatFilterstore().update1o1MFilter();
      return;
    } else {
      getCorePopUpStore().showAlertMessage(code);
    }
  };

  getUnreadMatchesConversations = async () => {
    const { alkhatabaUUID, userUUID } =
      getKhattabatMatchingStore().currentClientInformation.get();
    const { status, code, data } = await apiGetOneOOneMatchesConversations({
      alkhattabaUUID: alkhatabaUUID,
      clientUUID: userUUID,
      shouldUpdateUnread: true
    });
    if (status === "OK") {
      getKhattabatResultsStore().setClientConversations(data);
      return;
    } else {
      getCorePopUpStore().showAlertMessage(code);
    }
  };

  getMatches = (): TMatchesConversations => {
    if (getKhattabatFilterstore().searching1o1MMatches.get()) {
      return getKhattabatFilterstore().filtered101MArray.get();
    }
    return this.matchesConversations.get();
  };

  getFormatedMatchesConversationsComputed = computed(() => {
    let formatedArray = [];
    formatedArray = this.getMatches().map((matchDetails) => {
      const matchUUID = matchDetails.matchUUID;
      const messages = matchDetails.messages;
      const matchUserName = matchDetails.matchUserName;
      const matchStatus = matchDetails.matchStatus;
      const matchResidence = matchDetails.matchResidence;
      const matchNationality = matchDetails.matchNationality;
      const matchAge = matchDetails.matchAge;
      const matchHeight = matchDetails.matchHeight;
      const matchWeight = matchDetails.matchWeight;
      const matchLastLogin = matchDetails.matchLastLogin;
      const marriageType = matchDetails.marriageType;
      const isOpenedConversation = matchDetails.isOpenedConversation;
      const matchConversationID = getKhattabatStore().getOneOOneConversationsId(
        {
          alKhattabaUUID:
            getKhattabatMatchingStore().currentClientInformation.get()
              .alkhatabaUUID,
          userUUID: matchUUID
        }
      );
      const matchUnreadMessages = matchDetails.matchUnreadMessages;
      const matchConvesationIsDisabled =
        matchDetails.matchConvesationIsDisabled;
      const matchConversationIsHighlighted =
        matchDetails.matchConversationIsHighlighted;
      const matchComments = matchDetails.oneOOneMComments;
      const matchHasDeletedTheConversation =
        matchDetails.matchHasDeletedTheConversation;
      return {
        clientID:
          getKhattabatMatchingStore().currentClientInformation.get().userId,
        clientUUID:
          getKhattabatMatchingStore().currentClientInformation.get().userUUID,
        alkhattabaUUID:
          getKhattabatMatchingStore().currentClientInformation.get()
            .alkhatabaUUID,
        matchID: matchDetails.matchID,
        matchUUID,
        messages,
        matchUserName,
        matchStatus,
        matchResidence,
        matchNationality,
        matchAge,
        matchHeight,
        matchWeight,
        matchLastLogin,
        marriageType,
        isOpenedConversation,
        matchConversationID,
        matchUnreadMessages,
        matchConvesationIsDisabled,
        matchConversationIsHighlighted,
        matchComments,
        matchHasDeletedTheConversation
      };
    });
    const isFilterActivated =
      getKhattabatFilterstore().searching1o1MMatches.get();
    if (isFilterActivated) {
      return formatedArray;
    }
    const orderedFormatedArray = orderBy(
      formatedArray,
      [(obj) => obj.messages.length],
      "asc"
    );
    return orderedFormatedArray;
  });

  getCurrentMatchIds = () => {
    return this.getMatches().map((matchDetails) => {
      return matchDetails.matchID;
    });
  };

  removeMatchUser = (matchIds: number[]) => {
    matchIds.forEach((matchId) => {
      const filteredArray = this.matchesConversations
        .get()
        .filter((matchDetails) => {
          return matchDetails.matchID !== matchId;
        });
      runInAction(() => {
        this.matchesConversations.set(filteredArray);
      });
    });
  };

  getBasicMatchInformation = (matchIDs: number[]) => {
    const information = matchIDs.map((matchID) => {
      const currentMatchIndex = this.findMatchIndexInConversationArray({
        parameter: "matchID",
        parameterValue: matchID,
        matchesConversations: this.matchesConversations.get()
      });
      return {
        alkhattabaUUID:
          getKhattabatMatchingStore().currentClientInformation.get()
            .alkhatabaUUID,
        userUUID: this.matchesConversations.get()[currentMatchIndex].matchUUID,
        userID: matchID
      };
    });
    return information;
  };

  toggleMatchConversationOpenState = async ({
    matchID,
    alkhattabaUUID,
    clientUUID
  }: {
    matchID: number;
    alkhattabaUUID: string;
    clientUUID: string;
  }) => {
    const currentMatchIndex = this.findMatchIndexInConversationArray({
      parameter: "matchID",
      parameterValue: matchID,
      matchesConversations: this.matchesConversations.get()
    });
    const currentState =
      this.matchesConversations.get()[currentMatchIndex].isOpenedConversation;
    if (!currentState) {
      await this.updateOneOOneMMessages({
        alkhattabaUUID,
        clientUUID,
        clientID: matchID,
        shouldLoad: true
      });
    }
    runInAction(() => {
      this.matchesConversations.get()[currentMatchIndex].isOpenedConversation =
        !currentState;
    });
  };

  updateMatchConversationClientSide = ({
    matchID,
    messages,
    comments
  }: {
    matchID: number;
    messages: TKhattabatMessage[];
    comments: TKhattabatMessage[];
  }) => {
    const currentMatchIndex = this.findMatchIndexInConversationArray({
      parameter: "matchID",
      parameterValue: matchID,
      matchesConversations: this.matchesConversations.get()
    });
    runInAction(() => {
      this.matchesConversations.get()[currentMatchIndex].messages = messages;
      this.matchesConversations.get()[currentMatchIndex].oneOOneMComments =
        comments;
    });
  };

  updateClientOpenedOneOOneMConversations = () => {
    this.matchesConversations.get().forEach(async (matchDetails) => {
      const isOpenedConversation = matchDetails.isOpenedConversation;
      if (isOpenedConversation) {
        const clientUUID = matchDetails.matchUUID;
        await this.updateOneOOneMMessages({
          alkhattabaUUID:
            getKhattabatMatchingStore().currentClientInformation.get()
              .alkhatabaUUID,
          clientUUID,
          clientID: matchDetails.matchID,
          shouldLoad: false
        });
      }
    });
  };

  updateOneOOneMMessages = async ({
    alkhattabaUUID,
    clientUUID,
    clientID,
    shouldLoad
  }: {
    alkhattabaUUID: string;
    clientUUID: string;
    clientID: number;
    shouldLoad: boolean;
  }) => {
    getCoreUIStore().setIsLoading("KHATTABAT_MATCHING_RESULTS", shouldLoad);
    const { status, code, data } = await apiCallGetOneOOneMMessages({
      alkhattabaUUID,
      clientUUID
    });
    getCoreUIStore().setIsLoading("KHATTABAT_MATCHING_RESULTS", false);
    if (status === "OK") {
      this.updateMatchConversationClientSide({
        matchID: clientID,
        messages: data.oneOOneMMessages,
        comments: data.oneOOneMComments
      });
      getCoreTimingStore().initializeKhattabatPageMonitoring(
        "KHATTABAT_MATCHING_RESULTS"
      );
      return;
    } else {
      getCorePopUpStore().showAlertMessage(code);
    }
  };

  updateMatchConversationEnablementState = ({
    matchID,
    oldState
  }: {
    matchID: number;
    oldState: boolean;
  }) => {
    const currentMatchIndex = this.findMatchIndexInConversationArray({
      parameter: "matchID",
      parameterValue: matchID,
      matchesConversations: this.matchesConversations.get()
    });

    runInAction(() => {
      this.matchesConversations.get()[
        currentMatchIndex
      ].matchConvesationIsDisabled = !oldState;
    });
  };

  updateMatchConversationDeletionState = ({
    matchID,
    oldState
  }: {
    matchID: number;
    oldState: boolean;
  }) => {
    const currentMatchIndex = this.findMatchIndexInConversationArray({
      parameter: "matchID",
      parameterValue: matchID,
      matchesConversations: this.matchesConversations.get()
    });

    runInAction(() => {
      this.matchesConversations.get()[
        currentMatchIndex
      ].matchHasDeletedTheConversation = !oldState;
    });
  };

  disableEnableMatchConversationDatabase = async ({
    matchIDs,
    enableAllConversations,
    disableAllConversations
  }: {
    matchIDs: number[];
    enableAllConversations: boolean;
    disableAllConversations: boolean;
  }) => {
    let oneOOneMEnablementInformation: TOneOOneMEnablementInformation = [];
    matchIDs.forEach((matchID) => {
      const { alkhattabaUUID, userUUID } = this.getBasicMatchInformation([
        matchID
      ])[0];

      const currentMatchIndex = this.findMatchIndexInConversationArray({
        parameter: "matchID",
        parameterValue: matchID,
        matchesConversations: this.matchesConversations.get()
      });
      const oldState =
        this.matchesConversations.get()[currentMatchIndex]
          .matchConvesationIsDisabled;
      const newIsBlockedState = enableAllConversations
        ? 0
        : disableAllConversations
          ? 1
          : oldState
            ? 0
            : 1;
      oneOOneMEnablementInformation.push({
        alkhattabaUUID,
        userUUID,
        newIsBlockedState,
        userID: matchID
      });
    });
    const { status, code } = await apiCallSetOneOOneMEnablement({
      oneOOneMEnablementInformation
    });
    if (status === "OK") {
      matchIDs.forEach((matchID) => {
        const currentMatchIndex = this.findMatchIndexInConversationArray({
          parameter: "matchID",
          parameterValue: matchID,
          matchesConversations: this.matchesConversations.get()
        });
        const oldState = disableAllConversations
          ? false
          : enableAllConversations
            ? true
            : this.matchesConversations.get()[currentMatchIndex]
                .matchConvesationIsDisabled;

        this.updateMatchConversationEnablementState({
          matchID,
          oldState
        });
        getKhattabatFilterstore().update1o1MFilter();
      });
      return;
    } else {
      getCorePopUpStore().showAlertMessage(code);
    }
  };

  ignoreMatchConversationDatabase = async ({
    matchIDs
  }: {
    matchIDs: number[];
  }) => {
    const matchesInformation = this.getBasicMatchInformation(matchIDs);
    getCoreUIStore().setIsLoading("KHATTABAT_MATCHING_RESULTS", true);
    const { status, code } = await apiCallIgnoreOneOOneM({
      clientUUID:
        getKhattabatMatchingStore().currentClientInformation.get().userUUID,
      matchesInformation
    });
    getCoreUIStore().setIsLoading("KHATTABAT_MATCHING_RESULTS", false);
    if (status === "OK") {
      this.removeMatchUser(matchIDs);
      getKhattabatSelectsStore().resetSelectedConversations();
      getKhattabatFilterstore().update1o1MFilter();
    } else {
      getCorePopUpStore().showAlertMessage(code);
    }
  };

  getAllAvailableMatchesIds = computed(() => {
    let ids: number[] = [];
    if (isDefinedArray(this.matchesConversations.get())) {
      ids = this.matchesConversations.get().map((matchDetails) => {
        return matchDetails.matchID;
      });
    }
    return ids;
  });

  updateOneOOneMIsHighlightedDataBase = async ({
    conversationID,
    currentHightlightState,
    matchID
  }: {
    conversationID: string;
    matchID: number;
    currentHightlightState: boolean;
  }) => {
    const { status, code } = await apiCallSetOneOOneHighLight({
      conversationID,
      newState: currentHightlightState ? 0 : 1,
      conversationType: "101M"
    });
    if (status === "OK") {
      const matchIndex = this.findMatchIndexInConversationArray({
        parameter: "matchID",
        parameterValue: matchID,
        matchesConversations: this.matchesConversations.get()
      });
      if (isPositiveNumber(matchIndex, true)) {
        this.matchesConversations.get()[
          matchIndex
        ].matchConversationIsHighlighted = !currentHightlightState;

        getKhattabatFilterstore().update1o1MFilter();
      }
    } else {
      getCorePopUpStore().showAlertMessage(code);
    }
  };
}

export const getKhattabatResultsStore = memoize(
  () => {
    return new KhattabatResultsStore();
  },
  () => 1
);

export default getKhattabatResultsStore();
